import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMembership } from '../context/MembershipContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Send, MoreVertical, Ban, Flag, Camera, X, Check, CheckCheck, Trash2, Clock, Crown, Mic, Square, Play, Pause, Unlock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultAvatar = 'https://images.unsplash.com/photo-1635631191842-cc5043d56fc4?crop=entropy&cs=srgb&fm=jpg&q=85&w=100';

const Chat = () => {
  const { recipientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasFeature, checkFeature, getLimits, refresh: refreshMembership } = useMembership();
  const [recipient, setRecipient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [recipientTyping, setRecipientTyping] = useState(false);
  const [showExpiringPhoto, setShowExpiringPhoto] = useState(null);
  const [expiringCountdown, setExpiringCountdown] = useState(0);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [hasGrantedPrivateAccess, setHasGrantedPrivateAccess] = useState(false);
  const [grantingAccess, setGrantingAccess] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const photoInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioRefs = useRef({});

  useEffect(() => {
    fetchRecipient();
    findConversation();
    checkPrivateAccessStatus();
  }, [recipientId]);

  useEffect(() => {
    if (conversationId) {
      fetchMessages();
      // Poll for new messages and typing status
      const messageInterval = setInterval(fetchMessages, 3000);
      const typingInterval = setInterval(checkTypingStatus, 2000);
      return () => {
        clearInterval(messageInterval);
        clearInterval(typingInterval);
      };
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle typing indicator
  const handleTyping = () => {
    if (!isTyping) {
      setIsTyping(true);
      axios.put(`${API}/messages/typing`, { receiver_id: recipientId, is_typing: true }).catch(() => {});
    }
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      axios.put(`${API}/messages/typing`, { receiver_id: recipientId, is_typing: false }).catch(() => {});
    }, 3000);
  };

  const checkTypingStatus = async () => {
    try {
      const response = await axios.get(`${API}/messages/typing/${recipientId}`);
      setRecipientTyping(response.data.is_typing);
    } catch (error) {
      // Ignore errors
    }
  };

  const fetchRecipient = async () => {
    try {
      const response = await axios.get(`${API}/users/${recipientId}`);
      setRecipient(response.data);
    } catch (error) {
      toast.error('Failed to load user');
      navigate('/messages');
    }
  };

  const checkPrivateAccessStatus = async () => {
    try {
      // Check if I've already granted access to this user
      const response = await axios.get(`${API}/users/private-access/granted`);
      const grantedUsers = response.data.granted_to || [];
      const alreadyGranted = grantedUsers.some(u => u.id === recipientId);
      setHasGrantedPrivateAccess(alreadyGranted);
    } catch (error) {
      console.error('Failed to check private access status:', error);
    }
  };

  const handleSharePrivates = async () => {
    if (grantingAccess) return;
    setGrantingAccess(true);
    try {
      await axios.post(`${API}/users/private-access/grant/${recipientId}`);
      setHasGrantedPrivateAccess(true);
      toast.success(`You shared your private photos with ${recipient.display_name || recipient.username}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share private photos');
    } finally {
      setGrantingAccess(false);
    }
  };

  const handleRevokePrivates = async () => {
    if (grantingAccess) return;
    setGrantingAccess(true);
    try {
      await axios.post(`${API}/users/private-access/revoke/${recipientId}`);
      setHasGrantedPrivateAccess(false);
      toast.success('Private photo access revoked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    } finally {
      setGrantingAccess(false);
    }
  };

  const findConversation = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`);
      const conv = response.data.find(c => 
        c.participants.includes(recipientId)
      );
      if (conv) {
        setConversationId(conv.id);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!conversationId) return;
    try {
      const response = await axios.get(`${API}/messages/${conversationId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/messages/send`, {
        receiver_id: recipientId,
        content: newMessage.trim()
      });
      
      if (!conversationId) {
        setConversationId(response.data.conversation_id);
      }
      
      // Add message to local state immediately
      setMessages(prev => [...prev, {
        id: response.data.id,
        conversation_id: response.data.conversation_id,
        sender_id: user.id,
        receiver_id: recipientId,
        content: newMessage.trim(),
        timestamp: response.data.timestamp,
        is_read: false
      }]);
      
      setNewMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleBlock = async () => {
    try {
      await axios.post(`${API}/users/block`, { user_id: recipientId });
      toast.success('User blocked');
      navigate('/messages');
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const handleUnsend = async (messageId) => {
    // Check feature access
    if (!checkFeature('unsend_messages', 'Unsend messages')) return;
    
    try {
      await axios.delete(`${API}/messages/${messageId}`);
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, content: '[Message deleted]', unsent: true } : m
      ));
      toast.success('Message unsent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unsend message');
    }
  };

  const handlePhotoSelect = (e) => {
    // Check feature access before allowing photo selection
    if (!checkFeature('expiring_photos', 'Expiring photos')) {
      e.target.value = '';
      return;
    }
    
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedPhoto(reader.result);
      setShowPhotoDialog(true);
    };
    reader.readAsDataURL(file);
  };

  const sendExpiringPhoto = async () => {
    if (!selectedPhoto) return;
    
    // Double-check feature access
    if (!hasFeature('expiring_photos')) {
      toast.error('Expiring photos is a Premium feature. Upgrade to unlock!');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/messages/expiring`, {
        receiver_id: recipientId,
        photo_data: selectedPhoto,
        expires_in_seconds: 10
      });
      
      if (!conversationId) {
        setConversationId(response.data.conversation_id);
      }
      
      setMessages(prev => [...prev, {
        id: response.data.id,
        conversation_id: response.data.conversation_id,
        sender_id: user.id,
        receiver_id: recipientId,
        content: '[Expiring Photo]',
        message_type: 'expiring_photo',
        timestamp: response.data.timestamp,
        is_read: false
      }]);
      
      setSelectedPhoto(null);
      setShowPhotoDialog(false);
      refreshMembership(); // Refresh to update limits
      toast.success('Expiring photo sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send photo');
    } finally {
      setSending(false);
    }
  };

  const viewExpiringPhoto = async (message) => {
    if (message.expired || message.viewed) {
      toast.error('This photo has expired');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/messages/${message.id}/view-expiring`);
      setShowExpiringPhoto(response.data.photo_data);
      setExpiringCountdown(response.data.expires_in_seconds);
      
      // Start countdown
      const interval = setInterval(() => {
        setExpiringCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setShowExpiringPhoto(null);
            // Mark as expired
            axios.post(`${API}/messages/${message.id}/expire`).catch(() => {});
            // Update local state
            setMessages(prev => prev.map(m => 
              m.id === message.id ? { ...m, expired: true, viewed: true } : m
            ));
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to view photo');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Voice note recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Auto-stop at 60 seconds
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 60) {
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const sendVoiceNote = async () => {
    if (!audioBlob) return;
    
    setSending(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Audio = reader.result;
        
        const response = await axios.post(`${API}/messages/voice`, {
          receiver_id: recipientId,
          audio_data: base64Audio,
          duration: recordingTime
        });
        
        if (!conversationId) {
          setConversationId(response.data.conversation_id);
        }
        
        setMessages(prev => [...prev, {
          id: response.data.id,
          conversation_id: response.data.conversation_id,
          sender_id: user.id,
          receiver_id: recipientId,
          content: '[Voice Note]',
          message_type: 'voice_note',
          audio_data: base64Audio,
          duration: recordingTime,
          timestamp: response.data.timestamp,
          is_read: false
        }]);
        
        setAudioBlob(null);
        setRecordingTime(0);
        toast.success('Voice note sent!');
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send voice note');
    } finally {
      setSending(false);
    }
  };

  const toggleAudioPlayback = (messageId, audioSrc) => {
    const audioEl = audioRefs.current[messageId];
    if (!audioEl) return;
    
    if (playingAudioId === messageId) {
      audioEl.pause();
      setPlayingAudioId(null);
    } else {
      // Pause any currently playing audio
      if (playingAudioId && audioRefs.current[playingAudioId]) {
        audioRefs.current[playingAudioId].pause();
      }
      audioEl.play();
      setPlayingAudioId(messageId);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return defaultAvatar;
    if (photo.startsWith('data:') || photo.startsWith('http')) {
      return photo;
    }
    return `data:image/jpeg;base64,${photo}`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  if (loading || !recipient) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const recipientPhoto = recipient?.public_photos?.[0];

  return (
    <div className="h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 glass">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate('/messages')}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>

          <button
            data-testid="recipient-profile-btn"
            onClick={() => navigate(`/profile/${recipientId}`)}
            className="flex items-center gap-3 flex-1 min-w-0"
          >
            <div className="relative flex-shrink-0">
              <img
                src={getPhotoUrl(recipientPhoto)}
                alt={recipient.display_name || recipient.username}
                className="w-10 h-10 rounded-full object-cover"
              />
              {recipient.is_online && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-[#08D9D6] rounded-full border-2 border-[#050505]" />
              )}
            </div>
            <div className="text-left min-w-0">
              <h2 className="font-semibold text-white truncate">
                {recipient.display_name || recipient.username}
              </h2>
              <p className="text-xs text-[#A1A1AA]">
                {recipient.is_online ? 'Online' : 'Offline'}
              </p>
            </div>
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="chat-menu-btn"
                variant="ghost"
                size="icon"
                className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
              >
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10">
              <DropdownMenuItem 
                data-testid="view-profile-menu"
                onClick={() => navigate(`/profile/${recipientId}`)}
                className="text-[#EDEDED] focus:bg-white/10"
              >
                View Profile
              </DropdownMenuItem>
              {hasGrantedPrivateAccess ? (
                <DropdownMenuItem 
                  data-testid="revoke-privates-menu"
                  onClick={handleRevokePrivates}
                  disabled={grantingAccess}
                  className="text-[#A1A1AA] focus:bg-white/10"
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  {grantingAccess ? 'Processing...' : 'Revoke Private Access'}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem 
                  data-testid="share-privates-menu"
                  onClick={handleSharePrivates}
                  disabled={grantingAccess}
                  className="text-[#FF2E63] focus:bg-[#FF2E63]/10"
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  {grantingAccess ? 'Processing...' : 'Share My Privates'}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                data-testid="block-menu"
                onClick={handleBlock}
                className="text-red-500 focus:bg-red-500/10"
              >
                <Ban className="w-4 h-4 mr-2" />
                Block User
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <img
              src={getPhotoUrl(recipientPhoto)}
              alt={recipient.display_name || recipient.username}
              className="w-20 h-20 rounded-full object-cover mb-4"
            />
            <h3 className="text-lg font-semibold text-[#EDEDED] mb-1">
              {recipient.display_name || recipient.username}
            </h3>
            <p className="text-sm text-[#A1A1AA]">
              Start a conversation
            </p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              {/* Date separator */}
              <div className="flex items-center justify-center my-4">
                <span className="px-3 py-1 bg-white/5 rounded-full text-xs text-[#52525B]">
                  {date}
                </span>
              </div>

              {/* Messages for this date */}
              {dateMessages.map((message, index) => {
                const isSent = message.sender_id === user.id;
                const showAvatar = !isSent && (
                  index === 0 || 
                  dateMessages[index - 1]?.sender_id === user.id
                );
                const isExpiringPhoto = message.message_type === 'expiring_photo';
                const isUnsent = message.unsent;

                return (
                  <div
                    key={message.id}
                    className={`flex items-end gap-2 mb-2 ${isSent ? 'justify-end' : 'justify-start'}`}
                  >
                    {!isSent && (
                      <div className="w-8 flex-shrink-0">
                        {showAvatar && (
                          <img
                            src={getPhotoUrl(recipientPhoto)}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                      </div>
                    )}
                    
                    <div className={`max-w-[70%] group relative`}>
                      {/* Unsend button for sent messages */}
                      {isSent && !isUnsent && (
                        <button
                          onClick={() => handleUnsend(message.id)}
                          className={`absolute -left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded transition-opacity ${!hasFeature('unsend_messages') ? 'text-[#FFD700]' : ''}`}
                          title={hasFeature('unsend_messages') ? 'Unsend' : 'Elite feature - Unsend'}
                        >
                          <Trash2 className="w-4 h-4 text-[#A1A1AA]" />
                          {!hasFeature('unsend_messages') && (
                            <Crown className="w-3 h-3 text-[#FFD700] absolute -top-1 -right-1" />
                          )}
                        </button>
                      )}
                      
                      <div className={`${isSent ? 'message-sent' : 'message-received'} px-4 py-2 ${isUnsent ? 'opacity-50 italic' : ''}`}>
                        {/* Voice Note */}
                        {message.message_type === 'voice_note' ? (
                          <div className="flex items-center gap-3 min-w-[180px]">
                            <button
                              onClick={() => toggleAudioPlayback(message.id, message.audio_data)}
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isSent ? 'bg-white/20 hover:bg-white/30' : 'bg-[#FF2E63]/20 hover:bg-[#FF2E63]/30'
                              }`}
                            >
                              {playingAudioId === message.id ? (
                                <Pause className={`w-5 h-5 ${isSent ? 'text-white' : 'text-[#FF2E63]'}`} />
                              ) : (
                                <Play className={`w-5 h-5 ml-0.5 ${isSent ? 'text-white' : 'text-[#FF2E63]'}`} />
                              )}
                            </button>
                            <div className="flex-1">
                              <div className="flex items-center gap-1">
                                {[...Array(12)].map((_, i) => (
                                  <div 
                                    key={i} 
                                    className={`w-1 rounded-full ${isSent ? 'bg-white/40' : 'bg-[#FF2E63]/40'}`}
                                    style={{ height: `${Math.random() * 16 + 4}px` }}
                                  />
                                ))}
                              </div>
                              <span className={`text-xs ${isSent ? 'text-white/60' : 'text-[#52525B]'}`}>
                                {formatDuration(message.duration || 0)}
                              </span>
                            </div>
                            <audio
                              ref={el => audioRefs.current[message.id] = el}
                              src={message.audio_data}
                              onEnded={() => setPlayingAudioId(null)}
                              className="hidden"
                            />
                          </div>
                        ) : isExpiringPhoto && !message.expired ? (
                          <button
                            onClick={() => !isSent && viewExpiringPhoto(message)}
                            className={`flex items-center gap-2 ${isSent ? 'text-white/80' : 'text-[#FF2E63]'}`}
                          >
                            <Camera className="w-5 h-5" />
                            <span className="text-sm">
                              {isSent ? 'Expiring photo sent' : message.viewed ? 'Photo expired' : 'Tap to view photo'}
                            </span>
                            {!isSent && !message.viewed && (
                              <span className="text-xs bg-[#FF2E63] px-1.5 py-0.5 rounded text-white">10s</span>
                            )}
                          </button>
                        ) : isExpiringPhoto && message.expired ? (
                          <p className="text-[#52525B] text-sm italic flex items-center gap-2">
                            <Camera className="w-4 h-4" />
                            Photo expired
                          </p>
                        ) : (
                          <p className="text-white text-sm whitespace-pre-wrap break-words">
                            {message.content}
                          </p>
                        )}
                        
                        <div className={`flex items-center gap-1 mt-1 ${isSent ? 'justify-end' : ''}`}>
                          <p className={`text-xs ${isSent ? 'text-white/60' : 'text-[#52525B]'}`}>
                            {formatTime(message.timestamp)}
                          </p>
                          {/* Read receipts for sent messages */}
                          {isSent && !isUnsent && (
                            <span className="ml-1">
                              {message.is_read ? (
                                <CheckCheck className="w-4 h-4 text-[#08D9D6]" title="Read" />
                              ) : (
                                <Check className="w-4 h-4 text-white/40" title="Sent" />
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
        
        {/* Typing indicator */}
        {recipientTyping && (
          <div className="flex items-center gap-2 ml-10 mb-2">
            <div className="bg-white/10 rounded-2xl px-4 py-2 flex items-center gap-1">
              <div className="w-2 h-2 bg-[#A1A1AA] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-[#A1A1AA] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-[#A1A1AA] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
      </main>

      {/* Message Input */}
      <footer className="flex-shrink-0 glass p-4">
        {/* Voice Recording UI */}
        {isRecording || audioBlob ? (
          <div className="flex items-center gap-3">
            {isRecording ? (
              <>
                <div className="flex-1 flex items-center gap-3 px-4 py-2 bg-red-500/10 rounded-full border border-red-500/30">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-red-500 font-mono text-lg">{formatDuration(recordingTime)}</span>
                  <div className="flex-1 flex items-center gap-0.5">
                    {[...Array(20)].map((_, i) => (
                      <div 
                        key={i}
                        className="w-1 bg-red-500/60 rounded-full animate-pulse"
                        style={{ 
                          height: `${Math.random() * 20 + 8}px`,
                          animationDelay: `${i * 50}ms`
                        }}
                      />
                    ))}
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={cancelRecording}
                  variant="ghost"
                  className="h-12 w-12 rounded-full text-[#A1A1AA] hover:text-white hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </Button>
                <Button
                  type="button"
                  data-testid="stop-recording-btn"
                  onClick={stopRecording}
                  className="h-12 w-12 rounded-full bg-red-500 hover:bg-red-600"
                >
                  <Square className="w-5 h-5" />
                </Button>
              </>
            ) : (
              <>
                <div className="flex-1 flex items-center gap-3 px-4 py-2 bg-white/5 rounded-full">
                  <audio src={URL.createObjectURL(audioBlob)} controls className="flex-1 h-10" />
                  <span className="text-[#A1A1AA] text-sm">{formatDuration(recordingTime)}</span>
                </div>
                <Button
                  type="button"
                  onClick={cancelRecording}
                  variant="ghost"
                  className="h-12 w-12 rounded-full text-[#A1A1AA] hover:text-white hover:bg-white/10"
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
                <Button
                  type="button"
                  data-testid="send-voice-btn"
                  onClick={sendVoiceNote}
                  disabled={sending}
                  className="h-12 w-12 rounded-full bg-[#FF2E63] hover:bg-[#E01E50]"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={sendMessage} className="flex items-center gap-3">
            {/* Expiring photo button */}
            <input
              type="file"
              accept="image/*"
              ref={photoInputRef}
              onChange={handlePhotoSelect}
              className="hidden"
            />
            <Button
              type="button"
              data-testid="expiring-photo-btn"
              onClick={() => photoInputRef.current?.click()}
              variant="ghost"
              className={`h-12 w-12 rounded-full relative ${hasFeature('expiring_photos') ? 'text-[#A1A1AA] hover:text-[#FF2E63]' : 'text-[#52525B]'} hover:bg-white/10`}
              title={hasFeature('expiring_photos') ? 'Send expiring photo' : 'Premium feature - Expiring photos'}
            >
              <Camera className="w-5 h-5" />
              {!hasFeature('expiring_photos') && (
                <Crown className="w-3 h-3 text-[#FF2E63] absolute -top-0.5 -right-0.5" />
              )}
            </Button>
            
            {/* Voice note button */}
            <Button
              type="button"
              data-testid="voice-note-btn"
              onClick={startRecording}
              variant="ghost"
              className="h-12 w-12 rounded-full text-[#A1A1AA] hover:text-[#08D9D6] hover:bg-white/10"
              title="Send voice note"
            >
              <Mic className="w-5 h-5" />
            </Button>
            
            <Input
              data-testid="message-input"
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                handleTyping();
              }}
              placeholder="Type a message..."
              className="flex-1 bg-white/5 border-white/10 text-white h-12 placeholder:text-[#52525B]"
              disabled={sending}
            />
            <Button
              type="submit"
              data-testid="send-message-btn"
              disabled={!newMessage.trim() || sending}
              className="h-12 w-12 rounded-full bg-[#FF2E63] hover:bg-[#E01E50] disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>
        )}
      </footer>

      {/* Expiring Photo Preview Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#FF2E63]" />
              Send Expiring Photo
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedPhoto && (
              <img src={selectedPhoto} alt="Preview" className="w-full max-h-80 object-contain rounded-lg" />
            )}
            <p className="text-sm text-[#A1A1AA] mt-3 text-center">
              This photo will disappear after 10 seconds of viewing
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="ghost" 
              onClick={() => { setSelectedPhoto(null); setShowPhotoDialog(false); }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={sendExpiringPhoto}
              disabled={sending}
              className="flex-1 bg-[#FF2E63] hover:bg-[#E01E50]"
            >
              {sending ? 'Sending...' : 'Send Photo'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expiring Photo Viewer */}
      {showExpiringPhoto && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" onClick={() => {}}>
          <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/50 px-4 py-2 rounded-full">
            <Clock className="w-5 h-5 text-[#FF2E63]" />
            <span className="text-white font-bold text-xl">{expiringCountdown}s</span>
          </div>
          <img src={showExpiringPhoto} alt="Expiring" className="max-w-full max-h-full object-contain" />
          <p className="absolute bottom-8 text-white/60 text-sm">Photo will disappear in {expiringCountdown} seconds</p>
        </div>
      )}
    </div>
  );
};

export default Chat;
